import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isAndroid, isChrome } from 'react-device-detect'
import { Fade, Slide } from '@mui/material'
import { Box, Paper, type Theme, type SxProps, styled, type Sx } from '@dhl/theme'
import { Gesture } from '@dhl/gesture'

import { swipeDialogClasses } from './classes'
import type { SwipeDialogProps, ScrollableDialogBodyProps } from './types'
import { DialogHeader } from './components/DialogHeader'
import { getScrollingParent } from './get-scrolling-parent'

const inset = {
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
}

const styles: Record<string, Sx> = {
  container: {
    width: '100% !important',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  backdrop: {
    backgroundColor: (theme) => theme.palette.background.backdrop,
    position: 'absolute',
    ...inset,
  },
  backdropClose: {
    flex: 1,
    zIndex: 1300,
  },
  paper: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderBottomWidth: 0,
    boxShadow: 'none',
    backgroundImage: 'none',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
}

const Root = styled(Box)({
  position: 'fixed',
  zIndex: 1300,
  ...inset,
  overflow: 'hidden',
  display: 'flex',
  height: '100svh',
})

const DialogBody = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  position: 'relative',
})

const LinearGradient = styled(Box)(({ theme }) => ({
  background:
    theme.palette.mode === 'light'
      ? 'linear-gradient(0deg, #fff 15%, rgba(255, 255, 255, 0) 95%)'
      : 'linear-gradient(0deg, #262626 15%, rgba(38, 38, 38, 0) 95%)',
  width: '100%',
  minHeight: 48,
  position: 'sticky',
  bottom: `-${theme.spacing(4)}`,
  left: 0,
  zIndex: 1300,
}))

const ScrollableDialogBody = ({ dataTestId, onTouchStart, onTouchEnd }: ScrollableDialogBodyProps) =>
  function scrollableDialogBody({ sx, children, showGradient }: ScrollableDialogBodyProps) {
    return (
      <DialogBody
        data-testid={dataTestId}
        sx={{
          overflowY: 'auto',
          ...sx,
          pb: showGradient ? 4 : undefined,
          position: 'relative',
        }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {children}
        {showGradient && <LinearGradient />}
      </DialogBody>
    )
  }

export const SwipableDialogComponent = (props: SwipeDialogProps) => {
  const {
    children,
    dialogHeaderRef,
    minHeightPercentage = 100,
    preventScrollOnSwipe = false,
    onBack,
    onClose,
    onMove,
    disableSwipe,
    open,
    onOpen,
    onClosing,
    sx,
    title,
    closable = true,
    'data-testid': dataTestId,
  } = props
  const dialogRootRef = useRef<HTMLElement | null>(null)
  const [animation, setAnimation] = useState<'open' | 'close' | null>(null)
  const [swipeDisabled, setSwipeDisabled] = useState(disableSwipe)
  const transitionDuration = isAndroid && isChrome ? 600 : 200 // Currently the animation is glitchy on Android in the browser at lower transitiondurations
  const scrollingParent = useRef<HTMLElement | null>(null)
  const originalOverflow = useRef<string | undefined>(undefined)

  const testId = useCallback(
    (suffix?: string) => (dataTestId ? [dataTestId, suffix].filter(Boolean).join('-') : undefined),
    [dataTestId]
  )

  const resetOverflow = () => {
    if (scrollingParent.current) {
      if (originalOverflow.current !== undefined) {
        scrollingParent.current.style.overflow = originalOverflow.current
      } else {
        scrollingParent.current.style.removeProperty('overflow')
      }
      scrollingParent.current = null
    }
  }

  useEffect(() => {
    let rootRef: HTMLElement | null

    const rootElement = document.getElementById('root')

    if (dialogRootRef.current && open) {
      rootRef = dialogRootRef.current
      const parent = getScrollingParent(rootRef) ?? rootElement
      if (parent) {
        originalOverflow.current = parent.style.overflow
        parent.style.overflow = 'hidden'
        scrollingParent.current = parent
      }
    }
    return () => {
      resetOverflow()
    }
  }, [open])

  const ScrollableDialogBodyComponent = useMemo(
    () =>
      ScrollableDialogBody({
        onTouchStart: () => setSwipeDisabled(true),
        onTouchEnd: () => setSwipeDisabled(false),
        dataTestId: testId('body'),
      }),
    [setSwipeDisabled, testId]
  )

  const dialogMinHeight = useMemo(
    () => (minHeightPercentage === 0 ? 'auto' : `calc(1svh * ${minHeightPercentage})`),
    [minHeightPercentage]
  )

  // Support both render-prop and plain element children
  const childrenMemo = useMemo(() => {
    if (typeof children === 'function') {
      return children({
        ScrollableDialogBodyComponent,
      })
    }
    return children
  }, [children, ScrollableDialogBodyComponent])

  const handleClose = () => {
    resetOverflow()
    setAnimation('close')
    setTimeout(() => {
      onClose?.({}, 'backdropClick')
    }, transitionDuration)
  }

  const escalateClose = () => {
    setAnimation(null)
    resetOverflow()
    onClose?.({}, 'backdropClick')
  }

  if (open && !animation) {
    setAnimation('open')
    onOpen?.()
  } else if (!open && animation === 'open') {
    setAnimation('close')
  }

  if (!animation) {
    return null
  }

  return (
    <Root className={swipeDialogClasses.root} sx={sx} ref={dialogRootRef} data-testid={testId()}>
      <Fade in={animation === 'open'} timeout={transitionDuration}>
        <Box className={swipeDialogClasses.backdrop} sx={styles.backdrop} />
      </Fade>
      <Box sx={styles.container}>
        <Box sx={styles.backdropClose} data-testid="backdropclose" onClick={closable ? handleClose : undefined} />
        <Gesture.MoveDown
          disableSwipe={closable ? swipeDisabled : true}
          preventScrollOnSwipe={preventScrollOnSwipe}
          step={10}
          threshold={100}
          onClose={closable ? handleClose : undefined}
          onOpen={onOpen}
          onMove={onMove}
        >
          <Slide
            direction="up"
            in={animation === 'open'}
            timeout={transitionDuration}
            onExiting={onClosing}
            onExited={escalateClose}
          >
            <Paper sx={[styles.paper, { height: dialogMinHeight }] as SxProps<Theme>}>
              <DialogHeader
                ref={dialogHeaderRef}
                title={title}
                onClose={closable ? handleClose : undefined}
                onBack={onBack}
              />
              {childrenMemo}
            </Paper>
          </Slide>
        </Gesture.MoveDown>
      </Box>
    </Root>
  )
}
